import React, { useState } from "react";
import { toast } from "react-toastify";
import Modal from "../../../../components/Modal";
import { uploadExcelAction } from "../../../../actions/uploadExcelAction";
import { saveEntityLedgerAction } from "../../../../actions/saveEntityLedgerAction";
import "./index.scss";

const BulkLedgerModal = ({
  isOpen,
  onClose,
  selectedEntities = [],
  onSuccess,
  title,
}) => {
  const [file, setFile] = useState(null);
  const [uploadResponse, setUploadResponse] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setFile(null);
    setUploadResponse(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setUploadResponse(null);
    setUploading(true);

    // Collect all entity IDs from selected entities
    const entityIds = selectedEntities
      .map((row) => row.entity?.entityId || "")
      .filter(Boolean);

    // Upload file with multiple entity IDs
    const result = await uploadExcelAction({ entityIds, file: f });
    setUploading(false);

    if (result.success) {
      // Response is an array: [{ entityId, fileName, filePath, success, message }]
      setUploadResponse(result.data);
      const successCount =
        result.data?.filter((item) => item.success).length || 0;
      const failCount = (result.data?.length || 0) - successCount;

      if (failCount === 0) {
        toast.success(
          `File uploaded successfully for ${successCount} entit${successCount !== 1 ? "ies" : "y"}`,
        );
      } else {
        toast.warning(`Upload: ${successCount} succeeded, ${failCount} failed`);
      }
    } else {
      toast.error(result.error || "File upload failed");
      setFile(null);
    }
  };

  const canSubmit = uploadResponse && !submitting;

  const handleSubmit = async () => {
    if (!uploadResponse) {
      toast.error("Please upload a file first");
      return;
    }

    setSubmitting(true);

    // Build entity ledger list from selected entities and upload response
    const entityLedgerList = selectedEntities.map((row) => {
      const entity = row.entity || {};
      const entityId = entity.entityId || "";

      // Find matching upload response for this entity
      const uploadItem = Array.isArray(uploadResponse)
        ? uploadResponse.find((item) => item.entityId === entityId)
        : null;

      return {
        entityPrimaryId: entity.entityPrimaryId || entity.id || 0,
        entityId: entityId,
        isLedger: "true",
        ledgerName: uploadItem?.fileName || "",
        fileName: uploadItem?.fileName || "",
        filePath: uploadItem?.filePath || "",
      };
    });

    // Get user info for createdBy
    const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
    const createdBy = userInfo.name || userInfo.username || "";

    // Use first entity's email or empty string
    const firstEntity = selectedEntities[0]?.entity || {};
    const email =
      firstEntity.emailAddress || firstEntity.contactPersonEmail || "";

    // Call save-entity-ledger API
    const result = await saveEntityLedgerAction({
      entityLedgerList,
      isLedgerSendByEmail: false,
      createdBy,
      email,
    });

    setSubmitting(false);

    if (result.success) {
      toast.success(
        `Ledger submitted for ${selectedEntities.length} entit${selectedEntities.length !== 1 ? "ies" : "y"}!`,
      );
      handleClose();
      // Reset selection after successful upload
      if (onSuccess) {
        onSuccess();
      }
    } else {
      toast.error(result.error || "Failed to submit ledger");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      // title="Upload Ledger File"
      title={title || "Upload Ledger File"}
      size="medium"
    >
      <div className="blm-body">
        {/* ── Selected entities list ── */}
        <div className="blm-section">
          <p className="blm-section__label">
            Selected Entities
            <span className="blm-section__count">
              {selectedEntities.length}
            </span>
          </p>
          <div className="blm-entity-list">
            {selectedEntities.map((row, i) => {
              const e = row.entity || {};
              return (
                <div key={i} className="blm-entity-chip">
                  <span className="blm-entity-chip__id">
                    {e.entityId || "—"}
                  </span>
                  <span className="blm-entity-chip__name">
                    {e.entityName || e.EntityName || "—"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="blm-divider" />

        {/* ── Ledger fields ── */}
        <div className="blm-section">
          <p className="blm-section__label">Ledger Details</p>

          {/* File upload */}
          <div className="blm-field blm-field--file">
            <label className="blm-field__label">Document (Excel / PDF)</label>
            <label
              className={`blm-upload-zone ${uploadResponse ? "blm-upload-zone--done" : ""}`}
              htmlFor="blm-file-input"
            >
              <input
                id="blm-file-input"
                type="file"
                accept=".xlsx,.xls,.pdf"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              {uploading ? (
                <span className="blm-upload-zone__text">Uploading…</span>
              ) : file ? (
                <span className="blm-upload-zone__file">
                  📎 {file.name}
                  {uploadResponse && (
                    <span className="blm-upload-zone__ok"> ✓</span>
                  )}
                </span>
              ) : (
                <span className="blm-upload-zone__text">
                  Click to choose file
                </span>
              )}
            </label>
          </div>
        </div>

        {/* ── Actions ── */}
        <div className="blm-actions">
          <button className="blm-btn blm-btn--cancel" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="blm-btn blm-btn--submit"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {submitting
              ? `Submitting (${selectedEntities.length})…`
              : "Submit Ledger"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkLedgerModal;
